import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import type { OnboardingItemStatus } from './types';

export interface AssignableDoc {
  id: string;
  title: string;
  assigned: boolean;
  status: OnboardingItemStatus;
  /** False = no published version, so it cannot be assigned. */
  published: boolean;
}

/** Which documents a specific person is (or isn't) assigned to sign. */
export function usePersonDocuments(personId: string) {
  return useQuery({
    queryKey: ['person-documents', personId],
    queryFn: async () =>
      (
        await api<{ documents: AssignableDoc[] }>(
          `/api/admin/onboarding/people/${personId}/documents`,
        )
      ).documents,
  });
}

export function useSetPersonDocument(personId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ documentId, assigned }: { documentId: string; assigned: boolean }) =>
      api(`/api/admin/onboarding/people/${personId}/documents/${documentId}`, {
        method: assigned ? 'POST' : 'DELETE',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['person-documents', personId] });
      qc.invalidateQueries({ queryKey: ['person-onboarding', personId] });
    },
  });
}

export function useReopenPersonDocument(personId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (documentId: string) =>
      api(`/api/admin/onboarding/people/${personId}/documents/${documentId}/reopen`, {
        method: 'POST',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['person-documents', personId] });
      qc.invalidateQueries({ queryKey: ['person-onboarding', personId] });
    },
  });
}
